/**
 * Signup email-verification code helpers (backend#832).
 *
 * The backend keeps a code valid for 60 minutes by default and may report the
 * configured value as `expires_in_minutes` on the send-code responses. The
 * field is additive, so the UI has to work with and without it.
 */

/** Used whenever the backend does not report an expiry. */
export const DEFAULT_CODE_EXPIRY_MINUTES = 60;

/**
 * Normalise a reported expiry to whole minutes, or `null` when the value is
 * missing or not a usable positive number. The caller then keeps what it had,
 * which ends up as `DEFAULT_CODE_EXPIRY_MINUTES`.
 */
export const parseCodeExpiryMinutes = (value: unknown): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return Math.max(1, Math.round(value));
};

/** "60 minutes", "1 minute". */
export const formatExpiryMinutes = (minutes: number): string =>
  `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;

/** Case- and whitespace-insensitive email comparison. */
export const isSameEmail = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * How long the verification screen waits, without a successful verify, before
 * it shows the slow-mailbox hint. Most codes arrive within seconds, so an
 * immediate warning would be noise for most applicants; 45 s is long enough
 * that the mail is late, and short enough to come before the applicant gives
 * up or reaches for "resend" a second time.
 */
export const SLOW_MAIL_HINT_DELAY_MS = 45_000;

/**
 * Microsoft's consumer mail domains (hotmail.com, outlook.dk, live.co.uk,
 * msn.com …). They are the known slow case — Microsoft holds mail from
 * Brevo's shared IPs — so their users get the hint straight away.
 */
const SLOW_MAIL_DOMAIN = /@(hotmail|outlook|live|msn)\.[a-z.]+$/i;

export const isKnownSlowMailbox = (email: string): boolean => SLOW_MAIL_DOMAIN.test(email.trim());
